import { creditNoteApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetCreditNoteParamsI,
  ListCreditNoteI,
  OneCreditNoteForFrontI,
  NewCreditNoteI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getCreditNotes = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  typeCode,
  dateFrom,
  dateTo,
}: GetCreditNoteParamsI): Promise<ServiceResponseI<DataI<ListCreditNoteI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId) params.append("branchId", branchId);
    if (typeCode) params.append("typeCode", typeCode);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo)   params.append("dateTo",   dateTo);

    const { data } = await creditNoteApi.get<ServiceResponseI<DataI<ListCreditNoteI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const creditNoteById = async (
  id: string,
): Promise<ServiceResponseI<OneCreditNoteForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await creditNoteApi.get<ServiceResponseI<OneCreditNoteForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const creditNotePost = async (
  payload: NewCreditNoteI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await creditNoteApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const creditNoteAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await creditNoteApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useCreditNotes = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  typeCode,
  dateFrom,
  dateTo,
}: GetCreditNoteParamsI) => {
  const creditNotesQuery = useQuery(
    ["credit-notes", { pagination, sorting, globalFilter, branchId, typeCode, dateFrom, dateTo }],
    () => getCreditNotes({ pagination, sorting, globalFilter, branchId, typeCode, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData:     true,
    },
  );

  return { creditNotesQuery };
};